import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { fetchCompanyInfo } from '../services/companyInfo';

// ============================================================
// 직원 포털 사용 설명서 — 포털 내 직접 렌더링
// (구버전은 Google Docs 임베드였으나, 유지보수를 위해 코드로 전환)
// 버전 1.1 · 2026년 6월 — 위치 권한 안내(10장, 13.4) 추가
// ============================================================

const SECTIONS = [
  { id: 's1', no: 1, title: '들어가며 – 이 프로그램이 제공하는 가치' },
  { id: 's2', no: 2, title: '시스템 개요' },
  { id: 's3', no: 3, title: '로그인과 화면 구성' },
  { id: 's4', no: 4, title: '대시보드 – 한눈에 보는 현황' },
  { id: 's5', no: 5, title: '주문 관리' },
  { id: 's6', no: 6, title: '주문 생성' },
  { id: 's7', no: 7, title: '배차 호출과 신청 처리' },
  { id: 's8', no: 8, title: '운전자 관리' },
  { id: 's9', no: 9, title: '업체(고객) 관리' },
  { id: 's10', no: 10, title: '실시간 추적' },
  { id: 's11', no: 11, title: '알림 게시판' },
  { id: 's12', no: 12, title: '자주 묻는 질문(FAQ)' },
  { id: 's13', no: 13, title: '문제 해결 체크리스트' },
];

// ---------- 공통 빌딩 블록 ----------
const CALLOUT_STYLE = {
  tip:  { bg: '#e8f5e9', border: '#2e7d32' },
  warn: { bg: '#fff3e0', border: '#e65100' },
  lock: { bg: '#ede7f6', border: '#5e35b1' },
  info: { bg: '#e3f2fd', border: '#1565c0' },
};

function Callout({ type = 'info', title, children }) {
  const s = CALLOUT_STYLE[type] || CALLOUT_STYLE.info;
  return (
    <Box sx={{ my: 1.5, p: 1.5, bgcolor: s.bg, borderLeft: `4px solid ${s.border}`, borderRadius: 1 }}>
      {title && <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>{title}</Typography>}
      <Box sx={{ '& p': { m: 0 } }}>{children}</Box>
    </Box>
  );
}

function B({ items }) {
  return (
    <Box component="ul" sx={{ my: 0.5, pl: 2.5, '& li': { mb: 0.5 } }}>
      {items.map((t, i) => <Typography key={i} component="li" variant="body2">{t}</Typography>)}
    </Box>
  );
}

function T({ head, rows }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ my: 1.5 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            {head.map((h, i) => <TableCell key={i} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              {r.map((c, j) => <TableCell key={j}>{c}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function H2({ children }) {
  return <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2, mb: 0.5 }}>{children}</Typography>;
}

function P({ children }) {
  return <Typography variant="body2" sx={{ mb: 1 }}>{children}</Typography>;
}

function Section({ id, no, title, children }) {
  return (
    <Paper id={id} sx={{ p: 3, mb: 2, scrollMarginTop: 80 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, color: '#1565c0' }}>
        {no}. {title}
      </Typography>
      {children}
    </Paper>
  );
}

// ---------- 본문 ----------
function UserManual() {
  const [company, setCompany] = useState({});
  useEffect(() => { fetchCompanyInfo().then(setCompany).catch(() => {}); }, []);
  const cName = company.company_name || '';
  const cPhone = company.company_phone || '';

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <Box sx={{ maxWidth: 980, mx: 'auto' }}>
      {/* 표지 */}
      <Paper sx={{ p: 3, mb: 2, background: 'linear-gradient(135deg, #1565c0, #1e88e5)', color: '#fff' }}>
        <Typography variant="h5" fontWeight={800}>🚚 {cName} 직원 포털 사용 설명서</Typography>
        <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.85 }}>
          버전 1.1 · 2026년 6월{cPhone ? ` · 대표전화 ${cPhone}` : ''}
        </Typography>
      </Paper>

      {/* 목차 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>목차</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
          {SECTIONS.map((s) => (
            <Chip key={s.id} size="small" clickable label={`${s.no}. ${s.title}`} onClick={() => scrollTo(s.id)} />
          ))}
        </Box>
      </Paper>

      <Section id="s1" no={1} title="들어가며 – 이 프로그램이 제공하는 가치">
        <P>
          {cName} 직원 포털은 주문 접수부터 배차 호출, 배송 관리, 정산까지 물류 업무 전반을 하나의 화면에서
          처리할 수 있도록 설계된 통합 운영 시스템입니다. 직원분들이 반복 업무에 시간을 쓰지 않고
          고객과 운전자에게 집중할 수 있도록 다음과 같은 핵심 가치를 제공합니다.
        </P>
        <Callout type="info" title="✨ 핵심 가치 요약">
          <B items={[
            '① 한 화면에서 모든 배차 업무를 처리 – 주문 접수, 호출, 승인, 추적, 정산이 유기적으로 연결됩니다.',
            '② 실시간 알림 – 운전자의 배차 신청·배송 시작·배송 완료가 즉시 직원 포털로 푸시됩니다.',
            '③ 투명한 기록 – 모든 이벤트가 알림 게시판과 주문 이력에 자동으로 남습니다.',
            '④ 모바일 연동 – 운전자 전용 앱, 고객 포털과 완전히 연결된 3-way 커뮤니케이션.',
            '⑤ 실수를 줄이는 안전장치 – 상태 기반 버튼 노출, 확인 다이얼로그, 자동 검증.',
          ]} />
        </Callout>
        <H2>1.1 이 프로그램을 쓰면 달라지는 것</H2>
        <B items={[
          '[업무 속도] 전화·카톡·엑셀을 오가며 처리하던 배차 업무가 버튼 몇 번으로 완료됩니다.',
          '[정보 정확성] 출발지·목적지·차종·톤수·금액이 주문별로 구조화되어 기록되므로 누락과 오류가 줄어듭니다.',
          '[운전자 소통] 앱 푸시와 알림 게시판을 통해 운전자와 양방향 커뮤니케이션이 실시간으로 이뤄집니다.',
          '[추적성] 배송 시작 시각, 완료 시각, 송금 일시가 자동 기록되어 분쟁 발생 시 증거가 됩니다.',
          '[경영 가시성] 대시보드에서 매출·진행 중 주문·완료 주문을 한눈에 확인할 수 있습니다.',
        ]} />
        <H2>1.2 이 설명서의 범위</H2>
        <P>
          본 설명서는 "직원" 역할로 로그인하는 분들이 일상 업무에서 사용하는 기능만 다룹니다.
          시스템 관리, 직원 계정 관리, 데이터 백업 등 관리자 전용 기능은 포함하지 않습니다.
        </P>
      </Section>

      <Section id="s2" no={2} title="시스템 개요">
        <P>세 가지 사용자가 하나의 데이터 허브를 공유하는 구조로 동작합니다. 직원 포털은 이 허브의 관제탑 역할을 합니다.</P>
        <T head={['구성 요소', '사용자', '주요 역할']} rows={[
          ['직원 포털 (본 프로그램)', '직원', '주문 접수·호출·배차 승인·추적·정산'],
          ['운전자 앱 (안드로이드)', '운전자', '배차 신청, 배송 시작/완료 보고, 메시지'],
          ['고객 포털 (웹)', '고객(화주)', '주문 등록, 진행 상황 확인, 메시지'],
        ]} />
        <H2>2.1 주문의 생애주기</H2>
        <P>주문은 아래 상태를 순차적으로 거치며, 각 상태에서 할 수 있는 작업이 다릅니다.</P>
        <T head={['상태', '의미', '이 상태에서 하는 일']} rows={[
          ['가격 대기', '고객이 금액을 제시했으나 아직 확정되지 않음', '금액 협의 및 결재 확정'],
          ['배차 준비', '결재가 완료되어 운전자를 배정할 수 있는 상태', '배차 호출 시작 또는 직접 배차'],
          ['호출 중', '운전자들에게 주문이 공지된 상태', '신청자 검토 및 승인'],
          ['배차 완료', '특정 운전자에게 배차된 상태', '배송 시작 대기'],
          ['배송 중', '운전자가 배송을 시작한 상태', '실시간 위치 추적'],
          ['배송 완료', '운전자가 배송을 완료한 상태', '송금 처리'],
          ['송금 완료', '운전자에게 대금 지급이 완료된 상태', '주문 종료'],
        ]} />
      </Section>

      <Section id="s3" no={3} title="로그인과 화면 구성">
        <H2>3.1 로그인</H2>
        <B items={[
          '브라우저에서 직원 포털 주소로 접속합니다.',
          '관리자가 발급한 아이디와 비밀번호로 로그인합니다.',
          '첫 로그인 후에는 우측 상단 프로필 아이콘 → "비밀번호 변경"에서 본인만의 비밀번호로 변경하시기 바랍니다.',
        ]} />
        <Callout type="lock" title="🔐 보안 권고">
          <B items={[
            '사내 공용 PC에서 사용한 경우 반드시 로그아웃하세요.',
            '비밀번호를 타인과 공유하지 마세요. 각자의 활동이 기록에 남습니다.',
          ]} />
        </Callout>
        <H2>3.2 화면 구성</H2>
        <T head={['영역', '위치', '역할']} rows={[
          ['상단 바', '화면 맨 위', '로고, 내 계정, 비밀번호 변경/로그아웃'],
          ['사이드 메뉴', '왼쪽', '대시보드·주문·운전자·업체·추적·알림 게시판'],
          ['작업 영역', '가운데', '선택한 메뉴의 실제 화면이 표시되는 공간'],
          ['알림 팝업', '우측 상단', '운전자 이벤트가 발생하면 즉시 튀어나옴'],
        ]} />
        <P>
          모바일에서 접속하면 사이드 메뉴가 햄버거 버튼(☰)으로 접힙니다. PC에서는 햄버거 버튼을 누르면
          메뉴 영역을 좁게 접어 작업 공간을 넓힐 수 있습니다.
        </P>
      </Section>

      <Section id="s4" no={4} title="대시보드 – 한눈에 보는 현황">
        <P>로그인 직후 표시되는 화면으로, 오늘의 업무 상황을 요약해 보여줍니다.</P>
        <H2>주요 위젯</H2>
        <B items={[
          '① 오늘의 주문 건수 및 매출 합계',
          '② 진행 중인 주문 수 (호출 중 / 배차 완료 / 배송 중)',
          '③ 배차 대기 건 – 즉시 조치가 필요한 주문 목록',
          '④ 최근 알림 – 운전자 활동, 고객 문의 요약',
        ]} />
        <Callout type="tip" title="💡 활용 팁">
          <B items={[
            '업무 시작 시 먼저 대시보드를 보고 "배차 대기 건"이 있는지 확인하세요.',
            '각 숫자를 클릭하면 해당 조건으로 필터링된 주문 목록으로 바로 이동합니다.',
          ]} />
        </Callout>
      </Section>

      <Section id="s5" no={5} title="주문 관리">
        <P>사이드 메뉴의 "주문 관리"를 클릭하면 모든 주문이 표로 표시됩니다. 직원 포털에서 가장 많이 사용하는 화면입니다.</P>
        <H2>5.1 목록에서 볼 수 있는 정보</H2>
        <T head={['컬럼', '의미']} rows={[
          ['주문번호', '자동 생성되는 고유 번호'],
          ['회사명/담당자', '화주 정보'],
          ['출발지 → 목적지', '배송 구간'],
          ['차종/톤수', '요청된 차량'],
          ['금액', '제시금액(고객) / 배차금액(기사) / 수익금'],
          ['상태', '현재 주문 상태 (색상으로 구분됨)'],
          ['운전자', '배차된 운전자 이름 (미배차 시 "-")'],
          ['작업 버튼', '현재 상태에서 가능한 작업들'],
        ]} />
        <H2>5.2 필터와 검색</H2>
        <B items={[
          '상단의 금액 상태/배차 상태/업체 필터로 원하는 주문만 볼 수 있습니다.',
          '목록은 페이지 단위로 표시됩니다. 하단에서 페이지당 표시 건수를 조절할 수 있습니다.',
        ]} />
        <H2>5.3 자주 사용하는 작업 버튼</H2>
        <T head={['아이콘/버튼', '의미', '사용 시점']} rows={[
          ['📢 호출', '배차 호출 시작', '결재 완료 후 운전자를 찾을 때'],
          ['👤+ 직접 배차', '호출 없이 특정 기사 지정', '단골 기사에게 바로 배차할 때'],
          ['🔄 재호출', '이전 조건으로 다시 호출 (금액 조정 가능)', '배차 실패 후 재시도할 때'],
          ['👥 신청자', '신청한 운전자 목록 보기', '호출 중 상태에서 승인할 때'],
          ['▶ 배송 시작됨', '운전자가 출발했다는 표시', '(자동 업데이트)'],
          ['✅ 배송 완료', '배송이 끝났다는 표시', '(자동 업데이트)'],
          ['💰 송금 완료', '운전자에게 대금 지급 완료', '은행 송금 직후 체크'],
          ['💬 메시지', '해당 주문의 메시지 창 열기', '고객/운전자와 소통'],
          ['✏ 수정', '주문 정보 수정', '주소·금액·요청사항 변경'],
          ['❌ 취소', '주문 또는 배차 취소', '고객 요청으로 취소 시'],
        ]} />
        <Callout type="warn" title="⚠ 주의">
          <B items={[
            '배차 취소는 되돌릴 수 없습니다. 운전자에게 알림이 즉시 발송됩니다.',
            '송금 완료 체크는 실제 은행 송금을 마친 뒤에만 누르세요. 운전자에게 입금 알림이 전송됩니다.',
          ]} />
        </Callout>
      </Section>

      <Section id="s6" no={6} title="주문 생성">
        <P>고객의 주문을 시스템에 등록하는 화면입니다. 사이드 메뉴의 "주문 생성"을 클릭하세요.</P>
        <H2>6.1 입력 항목</H2>
        <T head={['항목', '설명', '필수']} rows={[
          ['업체 선택', '등록된 거래 업체 중 선택', '○'],
          ['담당자 이름·연락처', '자동으로 업체 정보에서 채워짐 (수정 가능)', '○'],
          ['출발지 주소', '주소 검색 버튼으로 다음 우편번호 이용', '○'],
          ['목적지 주소', '출발지와 동일한 방식', '○'],
          ['픽업 일시', '화주가 희망하는 상차 시각', '○'],
          ['차종', '카고 / 윙바디 / 탑 등 (복수 선택 가능)', '○'],
          ['톤수', '0.3t ~ 25t 중 선택 (복수 선택 가능)', '○'],
          ['금액', '배차 금액 (협의 중인 경우 후에 확정)', '○'],
          ['특이사항', '상/하차 작업 조건, 주의사항 등', '△'],
          ['사진 첨부', '화물 사진을 첨부하면 운전자에게 함께 전달됨', '△'],
        ]} />
        <H2>6.2 팁</H2>
        <B items={[
          '출발지·목적지 주소는 꼭 주소 검색 버튼을 사용하세요. 오타를 줄이고 운전자 앱의 지도 연동이 정확해집니다.',
          '화물 사진은 호출 시 운전자에게 함께 전달되므로 상차 형태를 미리 보여주는 데 도움이 됩니다.',
          '픽업 일시는 반드시 실제 가능한 시각으로 입력하세요. 운전자 앱에서 카운트다운/알림에 사용됩니다.',
        ]} />
      </Section>

      <Section id="s7" no={7} title="배차 호출과 신청 처리">
        <P>
          배차 호출은 시스템에 등록된 운전자들에게 주문을 공지하고 신청을 받는 절차입니다.
          주문이 "배차 준비" 상태일 때 📢 호출 버튼으로 시작합니다.
        </P>
        <H2>7.1 호출 다이얼로그에서 설정하는 것</H2>
        <B items={[
          '① 호출 금액',
          '② 호출 연락처 (운전자 문의 시 연락받을 번호)',
          '③ 요구 차종 – 여러 개 선택 가능 (예: 카고, 윙바디)',
          '④ 요구 톤수 – 여러 개 선택 가능 (예: 2.5t, 3.5t, 5t)',
          '⑤ 대상 – "전체" 또는 "특정 운전자만" 선택',
          '⑥ 출발지 반경 (거리 필터) – 전체 호출 시 출발지에서 일정 거리 안에 있는 기사에게만 호출',
        ]} />
        <Callout type="tip" title="💡 호출 대상 선택 기준">
          <B items={[
            '전체 호출: 빠른 매칭이 필요할 때. 조건에 맞는 모든 운전자에게 동시 공지됩니다.',
            '특정 호출: 신뢰하는 운전자나 해당 구간에 강점이 있는 운전자를 지정할 때 사용합니다.',
            '거리 필터: 마지막 위치가 출발지 반경 안인 기사에게만 호출합니다. 위치 정보가 없는 기사는 제외됩니다.',
          ]} />
        </Callout>
        <H2>7.2 신청 처리 (선착순)</H2>
        <B items={[
          '호출 후 운전자가 앱에서 신청하면 우측 상단에 실시간 알림이 표시됩니다.',
          '첫 신청자에게 자동으로 수락/포기 응답이 요청되며, 응답 시간 안에 수락하면 배차가 확정됩니다.',
          '👥 "신청자" 버튼으로 신청 목록을 보고 직접 승인할 수도 있습니다 (평점·차량번호·연락처 확인 가능).',
          '선정된 운전자에게는 "배차 확정" 푸시가, 나머지 신청자에게는 "미선정" 알림이 자동 전달됩니다.',
          '응답/유예 시간은 시스템 설정에서 조정할 수 있습니다 (기본 1분/3분).',
        ]} />
        <H2>7.3 호출 취소</H2>
        <P>
          호출을 시작했지만 취소가 필요한 경우(예: 고객이 주문 취소), 주문 행의 ❌ 버튼을 눌러 호출을
          취소할 수 있습니다. 이 경우 신청한 모든 운전자에게 취소 알림이 발송됩니다.
        </P>
      </Section>

      <Section id="s8" no={8} title="운전자 관리">
        <P>사이드 메뉴의 "운전자 관리"에서는 운전자 정보를 조회하고 배차에 필요한 정보를 확인·수정할 수 있습니다.</P>
        <H2>8.1 목록 화면</H2>
        <T head={['컬럼', '의미']} rows={[
          ['이름', '운전자 실명'],
          ['연락처', '운전자 휴대폰'],
          ['차량번호', '등록 차량'],
          ['차종/톤수', '보유 차량의 종류와 적재 중량'],
          ['평점', '완료한 배송에 대한 평균 평점'],
          ['상태', '활성 / 비활성 / 가입 대기'],
          ['작업', '정보 수정, 활성 토글, 평점 수정'],
        ]} />
        <H2>8.2 자주 하는 작업</H2>
        <B items={[
          '[승인] 새 운전자 가입 승인',
          '[수정] 차량 정보가 바뀐 경우 수정',
          '[비활성] 휴직/퇴사 등으로 당분간 배차에서 제외할 때 비활성화',
          '[평점] 배송 평가 결과를 반영해 평점 조정 (필요 시)',
        ]} />
        <Callout type="tip" title="💡 참고">
          <B items={[
            '비활성 상태인 운전자는 배차 호출 대상에서 자동으로 제외됩니다.',
            '운전자 본인은 앱에서 자신의 정보를 직접 수정할 수 있습니다. 이 경우 직원이 별도 조치할 필요는 없습니다.',
            '신규 기사님께는 고객포털의 [앱 설치 안내] 페이지를 보내주세요. 설치부터 위치 권한 설정까지 그림과 함께 안내됩니다.',
          ]} />
        </Callout>
      </Section>

      <Section id="s9" no={9} title="업체(고객) 관리">
        <P>사이드 메뉴의 "업체 관리"에서는 거래 업체(화주) 정보를 관리합니다. 이 정보는 주문 생성 시 업체 선택 드롭다운에 사용됩니다.</P>
        <H2>9.1 입력 항목</H2>
        <B items={['회사명', '사업자 등록번호', '담당자 이름·연락처·이메일', '주소', '결제 조건 / 비고']} />
        <H2>9.2 업체 등록 시 유의사항</H2>
        <B items={[
          '동일한 사업자번호의 중복 등록을 피하세요.',
          '담당자 연락처는 정확히 입력하세요. 고객포털 계정 생성/안내에 사용됩니다.',
          '거래가 종료된 업체는 삭제하지 말고 "비활성"으로 변경하세요. 과거 주문 이력이 남아 있어야 합니다.',
        ]} />
      </Section>

      <Section id="s10" no={10} title="실시간 추적">
        <P>
          사이드 메뉴의 "실시간 추적"에서는 현재 운행 중인 모든 운전자의 위치를 지도 위에서 확인할 수 있습니다.
          고객 문의에 즉시 대응하거나 배송 지연 여부를 미리 파악할 때 유용합니다.
        </P>
        <H2>사용 방법</H2>
        <B items={[
          '지도가 열리면 현재 배송 중 상태의 운전자들이 아이콘으로 표시됩니다.',
          '아이콘을 클릭하면 운전자 이름, 주문번호, 마지막 업데이트 시각을 볼 수 있습니다.',
          '운전자 앱이 백그라운드에서도 위치를 전송하므로 주기적으로 갱신됩니다.',
        ]} />

        <H2>운전자가 지도에 보이지 않을 때 (위치 권한)</H2>
        <P>
          운전자 위치 표시는 운전자 앱의 위치 권한에 의존합니다. 운전자가 지도에 나타나지 않거나
          위치가 오래된 경우 대부분 권한 문제입니다.
        </P>
        <B items={[
          '운전자 앱은 위치 권한이 "항상 허용"일 때만 백그라운드에서도 위치를 전송합니다. "앱 사용 중에만 허용" 상태면 앱을 닫는 순간 위치가 끊깁니다.',
          '휴대폰을 오래 사용하지 않으면 Android가 위치 권한을 자동으로 회수할 수 있습니다. 이 경우 운전자 앱 실행 시 "⚠️ 위치 권한이 꺼져 있습니다" 안내가 뜨며, [설정 열기] 버튼으로 바로 복구할 수 있습니다.',
          '권한 요청 창에서 [허용 안 함]을 두 번 누른 운전자는 권한 창이 다시 뜨지 않습니다. 이때는 휴대폰 설정에서 직접 변경해야 합니다 (아래 안내 문구 참고).',
          '위치 권한을 변경하면 Android 보안 정책상 앱이 자동으로 재시작될 수 있습니다. 고장이 아니므로 다시 실행하면 됩니다.',
        ]} />
        <Callout type="tip" title="💡 기사님께 안내할 때 (이대로 읽어주세요)">
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            "운전자앱 <strong>[설정] 탭 → '위치 액세스 권한' → [설정 열기]</strong>를 누르시고, 열린 화면에서
            <strong>'항상 허용'</strong>으로 바꿔 주세요. 같은 화면 아래의
            <strong>'사용하지 않는 동안 권한 제거(앱 일시중지)'는 꺼</strong> 주세요."
          </Typography>
          <Typography variant="body2">
            이 두 가지로 대부분 해결됩니다. 그림이 포함된 상세 안내는 고객포털 [앱 설치 안내] 페이지의
            "6단계: 위치 권한 설정"에 있습니다.
          </Typography>
        </Callout>

        <Callout type="warn" title="⚠️ 권한은 반드시 '앱 안에서' 변경하도록 안내하세요">
          <Typography variant="body2">
            휴대폰 설정 앱에서 곧바로 위치 권한을 바꾸면 변경 도중 운전자앱이 종료될 수 있습니다(Android 보안 정책).
            반드시 <strong>운전자앱 [설정] → '위치 액세스 권한' → [설정 열기]</strong>를 통해 변경하도록 안내하세요 —
            이 버튼은 권한 변경 전에 위치 전송을 잠시 멈춰 앱이 꺼지는 것을 막아줍니다.
          </Typography>
        </Callout>

        <Callout type="lock" title="🔒 개인정보 유의">
          <B items={[
            '운전자의 위치 정보는 배송 중에만 수집·표시됩니다.',
            '이 화면을 외부에 공유하거나 스크린샷을 외부로 전송하지 마세요.',
          ]} />
        </Callout>
      </Section>

      <Section id="s11" no={11} title="알림 게시판">
        <P>
          사이드 메뉴의 "알림 게시판"에서는 운전자와 주고받은 모든 이벤트 알림을 언제든 다시 확인할 수 있습니다.
          화면 우측 상단의 실시간 팝업은 몇 초 만에 사라지지만, 알림 게시판의 기록은 계속 남습니다.
        </P>
        <H2>11.1 두 가지 탭</H2>
        <T head={['탭', '내용']} rows={[
          ['📥 받은 알림', '운전자 → 직원 (배차 신청, 배송 시작, 배송 완료)'],
          ['📤 보낸 알림', '직원 → 운전자 (배차 호출, 배차 확정, 호출 취소, 배차 취소)'],
        ]} />
        <H2>11.2 알림 항목에 포함된 정보</H2>
        <B items={[
          '타입 칩 – 배차신청 / 배송시작 / 배송완료 / 배차호출 / 배차확정 / 취소',
          '제목 – 이모지가 포함된 짧은 요약',
          '본문 – [회사명] 출발지 → 목적지 | 운전자명 활동 형식',
          '관련 주문 번호와 운전자 이름',
          '발생 시각',
        ]} />
        <H2>11.3 조작</H2>
        <B items={[
          '✅ 한 건 읽음 처리 – 항목 오른쪽 아이콘',
          '✅ 전체 읽음 – 상단 버튼',
          '🗑 삭제 – 확인 후 영구 삭제됨',
          '🔄 새로고침 – 최신 알림 다시 불러오기',
        ]} />
        <Callout type="info" title="🔔 알림이 도착하는 경로">
          <B items={[
            '1) 화면 우측 상단에 파란색 스낵바로 즉시 팝업됩니다.',
            '2) 브라우저 알림 권한이 허용된 경우 운영체제 알림으로도 표시됩니다.',
            '3) 사이드 메뉴의 "알림 게시판" 아이콘에 숫자 배지로 읽지 않은 개수가 표시됩니다.',
            '4) 짧은 비프음이 재생되어 소리로도 알려줍니다.',
          ]} />
        </Callout>
      </Section>

      <Section id="s12" no={12} title="자주 묻는 질문 (FAQ)">
        {[
          ['Q1. 운전자가 배차 신청을 했는데 알림이 안 떠요.',
            '브라우저 탭이 완전히 닫혀 있으면 알림이 오지 않습니다. 최소한 하나의 직원 포털 탭을 열어두세요. 또한 브라우저 알림 권한이 "차단"되어 있으면 화면 안의 스낵바는 뜨지만 OS 알림은 오지 않습니다. 브라우저 설정에서 알림 권한을 "허용"으로 바꿔주세요.'],
          ['Q2. 송금 완료 버튼을 눌렀는데 "처리 실패" 오류가 나요.',
            '해당 주문이 "배차 완료" 이상의 상태이고, 운전자가 배정되어 있어야 합니다. 상태와 운전자를 확인한 뒤 다시 시도하세요. 반복된다면 관리자에게 문의하세요.'],
          ['Q3. 주문을 잘못 생성했어요. 어떻게 하나요?',
            '호출 이전이면 "주문 관리"에서 ✏ 수정 버튼으로 고칠 수 있습니다. 호출 이후라도 배차 전이면 ❌ 취소 후 새로 생성하시면 됩니다. 이미 배차된 건은 취소할 때 운전자에게 알림이 가므로 신중히 처리하세요.'],
          ['Q4. 같은 주문에 여러 운전자가 신청했어요. 어떤 기준으로 선택하나요?',
            '평점, 차량 적합성, 과거 거래 이력을 종합적으로 보고 판단합니다. 신청자 목록에서 평점과 차량번호를 함께 확인할 수 있습니다.'],
          ['Q5. 고객이 배송 현황을 물어봐요.',
            '"주문 관리"에서 해당 주문 행의 상태 컬럼과 💬 메시지 기능을 활용해 바로 답변할 수 있습니다. "실시간 추적"에서 운전자 현재 위치를 확인하여 예상 도착 시각을 안내하실 수도 있습니다.'],
          ['Q6. 알림음이 안 나와요.',
            '브라우저가 음소거 상태이거나 페이지와 상호작용이 한 번도 없으면 소리가 재생되지 않을 수 있습니다. 화면 어느 곳이든 한 번 클릭한 뒤 다시 시도해 보세요.'],
          ['Q7. 비밀번호를 잊었어요.',
            '관리자에게 초기화를 요청하세요. 관리자가 임시 비밀번호를 발급하면 로그인 후 즉시 본인이 변경하시기 바랍니다.'],
        ].map(([q, a], i) => (
          <Box key={i} sx={{ mb: 1.5 }}>
            <Typography variant="body2" fontWeight={700}>{q}</Typography>
            <Typography variant="body2" color="text.secondary">{a}</Typography>
          </Box>
        ))}
      </Section>

      <Section id="s13" no={13} title="문제 해결 체크리스트">
        <P>예상치 못한 동작을 만났을 때 다음 순서로 점검해 보시기 바랍니다.</P>
        <H2>13.1 화면이 갱신되지 않을 때</H2>
        <B items={[
          'Ctrl + F5 (강제 새로고침)',
          '브라우저 캐시 삭제 후 다시 로그인',
          '다른 브라우저(크롬, 엣지)로 접속하여 증상 재현 여부 확인',
        ]} />
        <H2>13.2 알림이 오지 않을 때</H2>
        <B items={[
          '우측 상단 자물쇠 아이콘에서 알림 권한 상태 확인 ("허용"이어야 함)',
          '사이드 메뉴 "알림 게시판"에 실제로 기록이 쌓이는지 확인',
          '기록은 있는데 팝업만 안 뜨면 브라우저 알림 차단 문제입니다.',
          '기록 자체가 없으면 운전자 측 앱 문제일 수 있으므로 관리자에게 문의하세요.',
        ]} />
        <H2>13.3 버튼을 눌러도 반응이 없을 때</H2>
        <B items={[
          '페이지 하단에 "처리 중..." 표시가 있는지 확인 (서버 응답 대기 중)',
          '10초 이상 응답이 없으면 새로고침',
          '반복되면 관리자에게 로그 확인 요청',
        ]} />
        <H2>13.4 운전자 위치가 표시되지 않을 때</H2>
        <B items={[
          '운전자 앱이 실행 중인지 확인 — 완전 종료 상태에서는 위치가 전송되지 않을 수 있습니다.',
          '운전자 휴대폰의 위치 권한이 "항상 허용"인지 확인하도록 안내하세요.',
          '권한 변경은 반드시 운전자앱 [설정] → "위치 액세스 권한" → [설정 열기]로 하도록 안내하세요. (설정 앱에서 직접 바꾸면 변경 중 앱이 종료될 수 있음)',
          '"사용하지 않는 동안 권한 제거(앱 일시중지)"가 켜져 있으면 끄도록 안내하세요.',
          '조치 후 앱을 완전히 종료했다가 다시 실행하게 한 뒤, 1분 안에 실시간 추적 화면에서 위치가 갱신되는지 확인하세요.',
          '자세한 권한 설정 방법은 "10. 실시간 추적" 장의 안내 문구를 참고하세요.',
        ]} />
        <H2>13.5 연락처</H2>
        <Callout type="info" title="📞 문의처">
          <B items={[
            ...(cPhone ? [`대표전화: ${cPhone}`] : []),
            ...(company.company_address ? [`주소: ${company.company_address}`] : []),
            '긴급 기술 문의는 시스템 관리자에게 직접 연락하세요.',
          ]} />
        </Callout>
      </Section>

      <Divider sx={{ my: 3 }} />
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        — 끝 — {cName} 직원 포털을 이용해 주셔서 감사합니다.
      </Typography>
    </Box>
  );
}

export default UserManual;
